/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import useSWR from 'swr';
import qs from 'qs';

import request from '@/utils/request';
import type * as Type from '@/common/interface';

export const useAdminAIMerchants = (params: Type.AIMerchantAdminListReq) => {
  const apiUrl = `/answer/admin/api/ai-merchants?${qs.stringify(params, {
    skipNulls: true,
  })}`;
  const { data, error, mutate } = useSWR<
    Type.ListResult<Type.AIMerchantItem>,
    Error
  >([apiUrl], request.instance.get);
  return {
    data,
    isLoading: !data && !error,
    error,
    mutate,
  };
};

export const saveAdminAIMerchant = (params: Type.AIMerchantAdminSaveReq) => {
  if (params.id) {
    return request.put<Type.AIMerchantItem>(
      '/answer/admin/api/ai-merchants',
      params,
    );
  }
  return request.post<Type.AIMerchantItem>(
    '/answer/admin/api/ai-merchants',
    params,
  );
};

export const setAdminAIMerchantBlacklist = (
  id: number,
  isBlacklisted: boolean,
) => {
  return request.put('/answer/admin/api/ai-merchants/blacklist', {
    id,
    is_blacklisted: isBlacklisted,
  });
};

export const useAdminAIMerchantReviews = (
  params: Type.AIMerchantReviewListReq | null,
) => {
  const apiUrl = params
    ? `/answer/admin/api/ai-merchants/reviews?${qs.stringify(params, {
        skipNulls: true,
      })}`
    : null;
  const { data, error, mutate } = useSWR<
    Type.ListResult<Type.AIMerchantReviewItem>,
    Error
  >(apiUrl ? [apiUrl] : null, request.instance.get);
  return {
    data,
    isLoading: Boolean(params) && !data && !error,
    error,
    mutate,
  };
};

export const updateAdminAIMerchantReview = (
  params: Type.AIMerchantAdminReviewUpdateReq,
) => {
  return request.put<Type.AIMerchantReviewItem>(
    '/answer/admin/api/ai-merchants/reviews',
    params,
  );
};

export const useAdminAIMerchantApplications = (
  params: Type.AIMerchantApplicationListReq,
) => {
  const apiUrl = `/answer/admin/api/ai-merchants/applications?${qs.stringify(
    params,
    { skipNulls: true },
  )}`;
  const { data, error, mutate } = useSWR<
    Type.ListResult<Type.AIMerchantApplicationItem>,
    Error
  >([apiUrl], request.instance.get);
  return {
    data,
    isLoading: !data && !error,
    error,
    mutate,
  };
};

export const auditAdminAIMerchantApplication = (
  params: Type.AIMerchantApplicationAuditReq,
) => {
  return request.put<Type.AIMerchantApplicationItem>(
    '/answer/admin/api/ai-merchants/applications',
    params,
  );
};

export const saveAdminAIMerchantReviewerProfile = (
  params: Type.AIMerchantReviewerProfileSaveReq,
) => {
  return request.put('/answer/admin/api/ai-merchants/reviewer-profile', params);
};
